import * as React from "react";
import SvgIcon from "@mui/material/SvgIcon";
import "./AISparkleIcon.css";

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function pick(prob) {
  return Math.random() < prob;
}

export default function AISparkleIcon(props) {
  const { notification = false, ...otherProps } = props;
  
  const tw = React.useMemo(() => {
    const bigMin = 5;
    const bigMaxTest = 9;
    // const bigMaxProd = 30;

    const bigDur = rand(bigMin, bigMaxTest);
    const bigDelay = rand(0.0, 1.5);
    const doubleFlash = pick(0.12);

    return {
      big: {
        "--twDur": `${bigDur.toFixed(2)}s`,
        "--twDelay": `${bigDelay.toFixed(2)}s`,
        "--twPeak": rand(0.90, 1.10).toFixed(2),
        "--twDouble": doubleFlash ? 1 : 0,
      },
      sm1: {
        "--twDur": `${rand(3.0, 5.2).toFixed(2)}s`,
        "--twDelay": `${rand(0.0, 3.2).toFixed(2)}s`,
        "--twPeak": rand(0.60, 0.90).toFixed(2),
      },
      sm2: {
        "--twDur": `${rand(3.0, 5.2).toFixed(2)}s`,
        "--twDelay": `${rand(0.0, 3.2).toFixed(2)}s`,
        "--twPeak": rand(0.60, 0.90).toFixed(2),
      },
    };
  }, []);

  return (
    <SvgIcon
      {...otherProps}
      viewBox="0 0 24 24"
      className={`AiSparkleIcon ${notification ? 'AiSparkleIcon--notification' : ''} ${props.className || ""}`.trim()}
      role="img"
      aria-label="AI features"
    >
      <defs>
        {/* Static purple haze (center bright -> edges transparent, so it fades into black) */}
        <radialGradient id="aiPurpleHaze" cx="50%" cy="50%" r="75%">
          <stop offset="0%" stopColor="rgba(168,85,247,0.80)" />
          <stop offset="25%" stopColor="rgba(168,85,247,0.40)" />
          <stop offset="35%" stopColor="rgba(168,85,247,0.10)" />
          <stop offset="100%" stopColor="rgba(168,85,247,0)" />
        </radialGradient>

        {/* Extra soft vignette mask to avoid any visible rect boundary */}
        <radialGradient id="aiHazeVignette" cx="50%" cy="50%" r="80%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="25%" stopColor="white" stopOpacity="0.5" />
          <stop offset="75%" stopColor="white" stopOpacity="0.1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>

        <mask
          id="aiHazeMask"
          maskUnits="userSpaceOnUse"
          maskContentUnits="userSpaceOnUse"
          x="-12"
          y="-12"
          width="48"
          height="48"
        >
          <rect x="-12" y="-12" width="48" height="48" fill="url(#aiHazeVignette)" />
        </mask>

        <filter id="aiHazeBlur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.1" />
        </filter>

        {/* Glow filter for orbiting particles */}
        <filter id="aiParticleGlow" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="0.8" />
        </filter>
      </defs>

      {/* Static purple haze background (oversized for glow to extend beyond bounds) */}
      <rect
        className="AiSparkleIcon-haze"
        x="-12"
        y="-12"
        width="48"
        height="48"
        fill="url(#aiPurpleHaze)"
        filter="url(#aiHazeBlur)"
        mask="url(#aiHazeMask)"
        pointerEvents="none"
      />

      {/* Orbiting particles (only visible in notification state) */}
      {notification && (
        <g className="AiSparkleIcon-particles">
          <circle
            className="AiSparkleIcon-particle AiSparkleIcon-particle--1"
            cx="12"
            cy="12"
            r="1"
            fill="#A855F7"
            filter="url(#aiParticleGlow)"
          />
          <circle
            className="AiSparkleIcon-particle AiSparkleIcon-particle--2"
            cx="12"
            cy="12"
            r="1"
            fill="#C084FC"
            filter="url(#aiParticleGlow)"
          />
          <circle
            className="AiSparkleIcon-particle AiSparkleIcon-particle--3"
            cx="12"
            cy="12"
            r="1"
            fill="#E9D5FF"
            filter="url(#aiParticleGlow)"
          />
        </g>
      )}

      {/* All stars properly centered - no transform needed */}
      <g className="AiSparkleIcon-stars">
        {/* Big star - better centered in viewBox */}
        <g className="AiSparkleIcon-starWrap" style={tw.big}>
          <path
            className="AiSparkleIcon-starCore AiSparkleIcon-starCore--big"
            d="M12 4.4 L13.2 7.8 L16.6 9 L13.2 10.2 L12 13.6 L10.8 10.2 L7.4 9 L10.8 7.8 Z"
          />

          <path
            className="AiSparkleIcon-starGlow AiSparkleIcon-starGlow--big AiSparkleIcon-starGlow--bigBurst"
            d="M12 4.4 L13.2 7.8 L16.6 9 L13.2 10.2 L12 13.6 L10.8 10.2 L7.4 9 L10.8 7.8 Z"
          />

          <path
            className="AiSparkleIcon-burstFill"
            d="M12 4.4 L13.2 7.8 L16.6 9 L13.2 10.2 L12 13.6 L10.8 10.2 L7.4 9 L10.8 7.8 Z"
          />
        </g>

        {/* Small star 1 - upper right */}
        <g className="AiSparkleIcon-starWrap" style={tw.sm1}>
          <path
            className="AiSparkleIcon-starCore AiSparkleIcon-starCore--sm"
            d="M16.4 10.4 L17.1 12.3 L19 13 L17.1 13.7 L16.4 15.6 L15.7 13.7 L13.8 13 L15.7 12.3 Z"
          />
          <path
            className="AiSparkleIcon-starGlow AiSparkleIcon-starGlow--sm"
            d="M16.4 10.4 L17.1 12.3 L19 13 L17.1 13.7 L16.4 15.6 L15.7 13.7 L13.8 13 L15.7 12.3 Z"
          />
        </g>

        {/* Small star 2 - lower center-right */}
        <g className="AiSparkleIcon-starWrap" style={tw.sm2}>
          <path
            className="AiSparkleIcon-starCore AiSparkleIcon-starCore--sm"
            d="M13.5 15.4 L14 16.7 L15.3 17.2 L14 17.7 L13.5 19 L13 17.7 L11.7 17.2 L13 16.7 Z"
          />
          <path
            className="AiSparkleIcon-starGlow AiSparkleIcon-starGlow--sm"
            d="M13.5 15.4 L14 16.7 L15.3 17.2 L14 17.7 L13.5 19 L13 17.7 L11.7 17.2 L13 16.7 Z"
          />
        </g>
      </g>
    </SvgIcon>
  );
}